import { clearStoredSession, getStoredToken } from '@/hooks/useAuthToken'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ErrorEnvelope {
  status: 'error'
  error: { code?: string; message?: string; detail?: unknown }
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return typeof value === 'object' && value !== null && 'error' in value
}

// Thin fetch wrapper for the legalflow-idp-hosted Config GUI backend.
// Endpoint functions (getCurrentBundle, publishBundle, ...) land in sibling
// files under src/api/ as the backend PRDs land in legalflow-idp.
export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getStoredToken()

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    const rawBody = await response.text()

    let code: string | undefined
    let detail: unknown
    let message = rawBody || response.statusText
    if (rawBody) {
      try {
        const parsed: unknown = JSON.parse(rawBody)
        if (isErrorEnvelope(parsed)) {
          code = parsed.error.code
          message = parsed.error.message ?? message
          detail = parsed.error.detail
        }
      } catch {
        // Not the backend's JSON error envelope - fall back to raw text.
      }
    }

    if (response.status === 401) {
      clearStoredSession()
    }

    throw new ApiError(response.status, message, code, detail)
  }

  // A successful PUT/DELETE may return an empty body (no success-body shape
  // is documented by PRD 21) - guard rather than let response.json() throw
  // on empty input.
  const rawBody = await response.text()
  return (rawBody ? JSON.parse(rawBody) : undefined) as T
}
